import React, { useState, useEffect } from "react";
import { 
    Box, 
    Typography, 
    Tooltip, 
    IconButton, 
    TextField, 
    Drawer, 
    Stack, 
    Button,
    Chip,
    InputAdornment,
    Autocomplete
} from "@mui/material";
import { CommonGridToolBarWithFilterText, NewCommonPatientDetails } from '../../../Components/Common Components/CommonComponents';
import { Colors } from "../../../Styles/Colors";
import { ImagePaths } from "../../../Utility/ImagePaths";
import ToastMsg from '../../../Components/ToastMsg/ToastMsg';
import RestAPIService from '../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../Utility/API/Serviceurls';
import CommonDatePicker from '../../../Components/Common Components/CommonDatePicker';
import { DateTime } from 'luxon';
import { formatDate, AmountFormat, ContentInTooltip } from '../../../Components/CommonFunctions/CommonFunctions';
import { AmountsCard } from '../../../Components/CommonCards/CommonCards';
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from '@mui/icons-material/Search';


const HistopathBillingReport = () => {
    const datelist = {
        fromDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
        toDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
        dateIndex: 1,
    }

    const [page, setpage] = useState(0);
    const [pageSize, setpageSize] = useState(10);
    const [reportData, setReportData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isloader, setisloader] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [errorMsg, seterrorMsg] = useState(false);
    const [errorMsgText, seterrorMsgText] = useState('');
    const [successMsg, setsuccessMsg] = useState(false);
    const [successMsgText, setsuccessMsgText] = useState('');
    const [filterData, setfilterData] = useState(datelist);
    const [searchFilters, setSearchFilters] = useState({
        patientSearch: '',
        invoiceSearch: '',
        uhid:'',
        paymentStatus:'All'
    });
    const [summaryData, setSummaryData] = useState([]);

    useEffect(() => {
        let isMounted = true;
        getReportData();
        
        return () => {
            isMounted = false;
        };
    }, []);


    const errorMessage = (msg) => {
        seterrorMsgText(msg);
        seterrorMsg(true);
    }

    const successMessage = (msg) => {
        setsuccessMsgText(msg);
        setsuccessMsg(true);
    }

    const generateUrl = () => {
      let url = `${Serviceurls.HISTOPATHOLOGY_BILL_REPORT}?from_date=${filterData.fromDate}&to_date=${filterData.toDate}`;

      // Add patient search filter if provided
      if (searchFilters.patientSearch.trim()) {
        url += `&patient_name=${encodeURIComponent(searchFilters.patientSearch.trim())}`;
      }

      // Add invoice search filter if provided
      if (searchFilters.invoiceSearch.trim()) {
        url += `&invoice_search=${encodeURIComponent(searchFilters.invoiceSearch.trim())}`;
      }
      if(searchFilters.uhid.trim()){
        url += `&uhid=${encodeURIComponent(searchFilters.uhid.trim())}`;
      }
      if(searchFilters.paymentStatus){
        url += `&payment_status=${encodeURIComponent(searchFilters.paymentStatus === "All" ? "" : searchFilters.paymentStatus)}`;
      }
      return url;
    }


    const getReportData = () => {
        let isMounted = true;
        
        try {
            setisloader(true);
            const url = generateUrl();
            RestAPIService.getAll(url)
                .then((response) => {
                    if (isMounted && response.data.status === 'success') {
                        const data = response.data.data || [];
                        const dataWithKeys = response.data || [];
                        setReportData(data);
                        setFilteredData(data); 
                        setSummaryData(dataWithKeys);
                        setisloader(false);
                    }
                }).catch(e => {
                    if (isMounted) {
                        setisloader(false);
                        if (e?.response?.data?.message) {
                            errorMessage(e.response.data.message);
                        } else {
                            errorMessage(e.message);
                        }
                    }
                });
        } catch (e) {
            if (isMounted) {
                setisloader(false);
                errorMessage(e.message);
            }
        }
        
        return () => {
            isMounted = false;
        };
    }


    const handleSearchChange = (field, value) => {
        setSearchFilters(prev => ({
            ...prev,
            [field]: value
        }));
    }

    const clearSearchFilters = () => {
        setSearchFilters({
            patientSearch: '',
            invoiceSearch: '',
            uhid:''
        });
        setFilteredData(reportData);
    }

    const getReportPrint = () => {
        const url = generateUrl() + "&export_type=pdf";
        try {
            RestAPIService.getAllPrint(url)
                .then((response) => {
                    const file = new Blob(
                        [response.data],
                        { type: 'application/pdf' });
                    const fileURL = URL.createObjectURL(file);
                    window.open(fileURL);
                }).catch((error) => {
                    errorMessage(error?.response?.data?.message || error?.message);
                });
        } catch (error) {
            errorMessage(error.message);
        }
    }

    const FilterDate = (from, to, index) => {
        setfilterData({ ...filterData, fromDate: from, toDate: to, dateIndex: index });
    }

    const renderFilterScreen = () => {
    return (
        <Box className='eMed_rts_filter_container' >
            <Box className='eMed_filter_Top' sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant='h6' fontWeight={600}>Filter</Typography>
                <Tooltip title="Close">
                    <Button onClick={() => { setFilterOpen(false) }}>
                        <img src={ImagePaths.LabCloseButton.default} alt="close" className='eMed_Canteen_CloseImg' />
                    </Button>
                </Tooltip>
            </Box>
            
            <Box className='eMed_rts_middle' sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Date Filter */}
                <Box className='eMed_date_filter'>
                    <CommonDatePicker
                        FromDate={filterData.fromDate}
                        ToDate={filterData.toDate}
                        DateIndex={filterData.dateIndex}
                        SelectedDates={FilterDate.bind(this)}
                        HideAllMenu={true} />
                </Box>
                
                {/* Patient Name Filter */}
                <Box>
                    <Typography variant="body2">
                        Patient Name
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter patient name"
                        value={searchFilters.patientSearch}
                        onChange={(e) => handleSearchChange('patientSearch', e.target.value)}
                    />
                </Box>
                
                {/* Invoice No Filter */}
                <Box>
                    <Typography variant="body2" >
                        Invoice No
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter invoice number"
                        value={searchFilters.invoiceSearch}
                        onChange={(e) => handleSearchChange('invoiceSearch', e.target.value)}
                    />
                </Box>
                <Box>
                    <Typography variant="body2" >
                        UHID
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter UHID number"
                        value={searchFilters.uhid}
                        onChange={(e) => handleSearchChange('uhid', e.target.value)}
                    />
                </Box>
                <Box>
                    <Typography varient='body2'>
                        Payment Status
                    </Typography>
                    <Autocomplete
                        size="small"
                        disableClearable
                        options={['All','Paid', 'Not paid', 'Partial']}
                        value={searchFilters.paymentStatus || null}
                        onChange={(e, value) => handleSearchChange('paymentStatus', value)}
                        renderInput={(params) => <TextField {...params} size="small" placeholder="Select Payment Status" />}
                    />
                </Box>
            </Box>
            
            <Box >
                <Stack spacing={2} direction="row" justifyContent={'center'}>
                    <Button 
                        size='small' 
                        variant='outlined' 
                        className='eMed_Canteen_BtnText' 
                        onClick={() => { 
                            setfilterData(datelist);
                            setSearchFilters({
                                patientSearch: '',
                                invoiceSearch: '',
                                uhid:'',
                                paymentStatus:'All'
                            });
                        }}
                    >
                        Clear All
                    </Button>
                    <Button 
                        size='small' 
                        variant='contained' 
                        className='eMed_Canteen_BtnText' 
                        onClick={() => {
                            setfilterData(prev => ({
                                ...prev,
                                patientSearch: searchFilters.patientSearch,
                                invoiceSearch: searchFilters.invoiceSearch,
                                uhid: searchFilters.uhid,
                                paymentStatus: searchFilters.paymentStatus,
                            }));
                            getReportData();
                            setFilterOpen(false);
                        }}
                    >
                        Search
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
}


    const closeFilter = () => {
        setFilterOpen(false);
    }

    const tableView = () => {
        const gridToolBar = () => {
            let TextArray = [
                { label: "From Date", value: formatDate(filterData.fromDate) },
                { label: "To Date", value: formatDate(filterData.toDate) },
            ];

            return (
                <Box>
                    <CommonGridToolBarWithFilterText FilterTextArray={TextArray} />
                </Box>
            );
        }

        const dataWithSno = (Array.isArray(reportData) ? reportData : []).map((item, index) => ({
            ...item,
            sno: index + 1
        })) || [];

        const columns = [
            {
                field: 'sno', 
                headerName: 'S No', 
                flex: 0.03,
                renderCell: (params) => (<Box component={'div'}>{params?.row?.sno || '-'}</Box>)
            },
            {
                field: 'patient_name',
                headerName: 'Patient Details',
                flex: 0.13,
                minWidth: 200,
                renderCell: (params) => (<NewCommonPatientDetails disablePhoto={true} data={params?.row} />)
            },
            {
                field: 'bill_date',
                headerName: 'Bill Date & Time',
                flex: 0.1,
                minWidth: 140,
                renderCell: (params) => (
                    <Typography variant="body2">
                        {params?.row?.bill_date || '-'}
                    </Typography>
                )
            },
            {
                field: 'credit_type',
                headerName: 'Credit Details',
                flex: 0.1,
                minWidth: 140,
                renderCell: (params) => (
                    <Box variant="body2">
                        {ContentInTooltip(params?.row?.credit_type || '-', 20, '0.9vw')}
                        {ContentInTooltip(params?.row?.employer_name || '-', 20, '0.8vw')}
                    </Box>
                )
            },
            {
                field: 'referred_type',
                headerName: 'Referral Details',
                flex: 0.1,
                minWidth: 140,
                renderCell: (params) => (
                    <Box variant="body2">
                        {ContentInTooltip((params?.row?.referred_doctor_name ? "Doctor" : "Self"), 20, '0.9vw')}
                        {ContentInTooltip((params?.row?.referred_doctor_name ? (`${params?.row?.referred_doctor_name} ${params?.row?.referred_hospital_name ? `- ${params?.row?.referred_hospital_name}` : ""}`)  : ''), 20, '0.8vw')}
                    </Box>
                )
            },
            {
                field: 'bill_no',
                headerName: 'Bill No',
                flex: 0.1,
                minWidth: 100,
                renderCell: (params) => (
                    <Tooltip placement='top' title={params?.row?.bill_no} arrow>
                        <Box component={'div'}>{params?.row?.bill_no ? '...' + params?.row?.bill_no?.slice(-10) : "-"}</Box>
                    </Tooltip>
                )
            },
            {
                field: 'specimen',
                headerName: 'Specimen',
                flex: 0.08,
                minWidth: 100,
                renderCell: (params) => (
                    <Typography variant="body2">
                        {ContentInTooltip(params?.row?.specimen || '-',15)}
                    </Typography>
                )
            },
            {
                field: 'sample_id',
                headerName: 'Sample ID',
                flex: 0.08,
                minWidth: 100,
                renderCell: (params) => (
                    <Typography variant="body2">
                        {ContentInTooltip(params?.row?.sample_id || '-', 15)}
                    </Typography>
                )
            },
            {
                field: 'overall_sample_sequence',
                headerName: 'Overall Sample Sequence',
                flex: 0.1,
                minWidth: 130,
                renderCell: (params) => (
                    <Typography variant="body2">
                        {ContentInTooltip(params?.row?.overall_sample_sequence || '-', 15)}
                    </Typography>
                )
            },
            {
                field: 'payment_status',
                headerName: 'Payment Status',
                flex: 0.08,
                minWidth: 120,
                renderCell: (params) => (
                    <Typography variant="body2">
                        {params.value || '-'}
                    </Typography>
                )
            },
            {
                field: 'bill_amount',
                headerName: 'Amount',
                flex: 0.08,
                minWidth: 100,
                renderCell: (params) => (
                    <Typography variant="body2" fontWeight="bold">
                        {AmountFormat(params?.row?.bill_amount || 0)}
                    </Typography>
                )
            },
        ];

        return (
            <Box sx={{ height: '67vh', marginTop: '.5vw', backgroundColor: 'white' }}>
                <DataGrid
                    rows={dataWithSno}
                    columns={columns}
                    getRowId={(row) => row.bill_summary_id || row.sno}
                    page={page}
                    pageSize={pageSize}
                    rowCount={filteredData.length}
                    localeText={{
                        toolbarColumns: "",
                        toolbarDensity: "",
                        toolbarExport: "",
                        toolbarFilters: "",
                        toolbarExportPrint: ""
                    }}
                    components={{
                        Toolbar: gridToolBar,
                        NoRowsOverlay: () => (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%'
                            }}>
                                {'No Records To Be Shown'}
                            </Box>
                        )
                    }}
                    onPageChange={(newPage) => { setpage(newPage) }}
                    onPageSizeChange={(newPageSize) => { setpageSize(newPageSize) }}
                    rowsPerPageOptions={[10, 20, 30]}
                    pagination
                    paginationMode="client"
                    headerHeight={40}
                    density='standard'
                    disableSelectionOnClick
                    loading={isloader}
                />
            </Box>
        );
    }

    const msgClose = () => {
        seterrorMsg(false);
        seterrorMsgText("");
        setsuccessMsg(false);
        setsuccessMsgText("");
    }

    return (
        <Box sx={{ 
            height: '79.5vh', 
            width: '94vw', 
            backgroundColor: Colors.Background, 
            padding: '.5vw' 
        }}>
            {/* Summary Cards */}
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2} gap={2}>
                <Box display={'flex'} flexDirection={'row'} gap={2} flex={1} marginLeft={'-.8vw'}>
                    {AmountsCard('Total Amount', AmountFormat(summaryData.total_bill_amount), true)}
                    {AmountsCard('Total Bills', summaryData.total_bill_count, true)}
                </Box>
                <Box>
                    <IconButton onClick={() => { setFilterOpen(true) }}>
                        <img 
                            style={{ width: '2vw', height: '2vw' }} 
                            src={ImagePaths.Filter.default} 
                            alt='filter' 
                        />
                    </IconButton>
                    <IconButton onClick={getReportPrint}>
                        <img 
                            style={{ width: '2vw', height: '2vw' }} 
                            src={ImagePaths.LabPrintIcon.default} 
                            alt='print' 
                        />
                    </IconButton>
                </Box>
            </Box>
            
            {/* Table Section */}
            <Box>
                {tableView()}
            </Box>
            
            {/* Filter Drawer */}
            <Drawer
                anchor={'right'}
                open={filterOpen}
                onClose={closeFilter}
                ModalProps={{ onBackdropClick: closeFilter }}
                sx={{height: '49vw'}}
            >
                {renderFilterScreen()}
            </Drawer>
            
            {/* Toast Messages */}
            {errorMsg &&
                <ToastMsg
                    severity={'error'}
                    msg={errorMsgText}
                    msgPop={msgClose.bind(this)}
                />
            }
            {successMsg &&
                <ToastMsg
                    severity={'success'}
                    msg={successMsgText}
                    msgPop={msgClose.bind(this)}
                />
            }
        </Box>
    );
}

export default HistopathBillingReport;